import { useEffect, useRef, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, LogIn, LogOut, X, Users as UsersIcon, Clock, Download, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};
const firstOfMonth = (dateStr) => `${dateStr.slice(0, 7)}-01`;

const STATUS_META = {
  COMPLETED: { label: "Completed", bg: "bg-[#22A06B]", text: "text-white", Icon: CheckCircle2 },
  IN_ONLY: { label: "In · not out yet", bg: "bg-[#FFB800]", text: "text-black", Icon: Clock },
  NOT_PUNCHED: { label: "Not punched", bg: "bg-zinc-200", text: "text-zinc-700", Icon: XCircle },
};

export default function Attendance() {
  const [today, setToday] = useState(null);
  const [showCam, setShowCam] = useState(null); // "IN" | "OUT"
  const [dateFrom, setDateFrom] = useState(firstOfMonth(todayIST()));
  const [dateTo, setDateTo] = useState(todayIST());
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);

  async function loadToday() {
    try { const { data } = await api.get("/attendance/today"); setToday(data); } catch (_) {}
  }
  async function loadMatrix() {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/report", { params: { date_from: dateFrom, date_to: dateTo } });
      setMatrix(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadToday(); }, []);
  useEffect(() => { loadMatrix(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  async function downloadReport() {
    try {
      const res = await api.get("/attendance/report.xlsx", { params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `closet-attendance-${dateFrom}-to-${dateTo}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
      toast.success("Attendance Excel downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="attendance-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ HR · SELFIE ATTENDANCE</div>
          <h1 className="section-title text-5xl lg:text-6xl">Attendance.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Punch in and out with a live selfie. One IN + one OUT per day. Late = punched after your shift-in time.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowCam("IN")} className="brut-btn px-5 py-2.5 inline-flex items-center gap-2" data-testid="punch-in-btn">
            <LogIn size={16} /> Punch IN
          </button>
          <button onClick={() => setShowCam("OUT")} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2" data-testid="punch-out-btn">
            <LogOut size={16} /> Punch OUT
          </button>
        </div>
      </header>

      {/* Today strip */}
      {today && (
        <div className="brut-card p-5 mb-6" data-testid="today-strip">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon size={14} className="text-zinc-500" />
            <div className="font-mono-ds text-[11px] tracking-widest uppercase text-zinc-500">Today · {today.date}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {today.rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.NOT_PUNCHED;
              const Icon = meta.Icon;
              return (
                <div key={r.employee_id} className={`brut-card-flat p-3 ${(r.in?.face_verified === false || r.out?.face_verified === false) ? "bg-[#FFF0F0] border-[#FF3333]" : "bg-white"}`} data-testid={`today-row-${r.employee_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 flex flex-col gap-1">
                      <SelfieThumb rec={r.in} label="IN" data-testid={`selfie-in-${r.employee_id}`} />
                      <SelfieThumb rec={r.out} label="OUT" data-testid={`selfie-out-${r.employee_id}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-display font-bold text-sm truncate">{r.employee_name}</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 border-2 border-black font-mono-ds text-[9px] font-bold uppercase ${meta.bg} ${meta.text}`}><Icon size={10} /> {meta.label}</span>
                      </div>
                      <div className="font-mono-ds text-[10px] text-zinc-500 uppercase">
                        {r.designation || "—"} · Shift {r.shift_in_time} → {r.shift_out_time}
                      </div>
                      <div className="font-mono-ds text-xs mt-2 flex flex-wrap gap-x-4 gap-y-1">
                        <span>IN: <b>{r.in ? r.in.time : "—"}</b>{r.in?.is_late && <span className="ml-1 text-[#FF3333]">(late)</span>}</span>
                        <span>OUT: <b>{r.out ? r.out.time : "—"}</b>{r.out?.is_early_leave && <span className="ml-1 text-[#FF3333]">(early)</span>}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Report */}
      <div className="brut-card p-4 mb-4 flex flex-wrap gap-3 items-center" data-testid="attendance-filter">
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">From</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="attendance-date-from" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">To</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="attendance-date-to" />
        <button onClick={loadMatrix} className="brut-btn-outline px-3 py-2 inline-flex items-center gap-2" data-testid="attendance-refresh"><RefreshCw size={12} /> Refresh</button>
        <button onClick={downloadReport} className="brut-btn px-3 py-2 inline-flex items-center gap-2 ml-auto" data-testid="attendance-download-xlsx"><Download size={12} /> Download Excel</button>
      </div>

      {loading ? (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">Loading…</div>
      ) : matrix && matrix.users.length > 0 ? (
        <div className="brut-card overflow-x-auto" data-testid="attendance-matrix">
          <table className="brut-table" style={{ minWidth: `${400 + matrix.dates.length * 90}px` }}>
            <thead>
              <tr>
                <th className="text-left sticky left-0 bg-white z-10">Employee</th>
                <th>Present</th><th>Absent</th><th>Late</th><th>Early</th>
                {matrix.dates.map((d) => <th key={d} className="text-center whitespace-nowrap font-mono-ds text-[10px]">{d.slice(5)}</th>)}
              </tr>
            </thead>
            <tbody>
              {matrix.users.map((u) => (
                <tr key={u.id} data-testid={`matrix-row-${u.id}`}>
                  <td className="font-display font-bold sticky left-0 bg-[#FDF6EE]">{u.name}<div className="font-mono-ds text-[9px] text-zinc-500">{u.designation}</div></td>
                  <td className="font-mono-ds font-bold text-[#22A06B]">{u.days_present}</td>
                  <td className="font-mono-ds text-zinc-500">{u.days_absent}</td>
                  <td className="font-mono-ds text-[#FF3333]">{u.late_count}</td>
                  <td className="font-mono-ds text-[#FFB800]">{u.early_count}</td>
                  {matrix.dates.map((d) => {
                    const rec = u.daily[d];
                    const bg = rec.status === "COMPLETED" ? "bg-[#E7F5EE]" : rec.status === "IN_ONLY" ? "bg-[#FFF8DC]" : rec.status === "OUT_ONLY" ? "bg-[#FFE0D0]" : "bg-white";
                    return (
                      <td key={d} className={`text-center font-mono-ds text-[10px] ${bg}`}>
                        {rec.in && <div>IN {rec.in.time}{rec.in.is_late && "·L"}</div>}
                        {rec.out && <div>OUT {rec.out.time}{rec.out.is_early_leave && "·E"}</div>}
                        {!rec.in && !rec.out && <span className="text-zinc-400">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="brut-card p-10 text-center font-mono-ds text-xs uppercase text-zinc-500">No attendance data in range</div>
      )}

      {showCam && <SelfieCam type={showCam} onClose={() => setShowCam(null)} onDone={() => { setShowCam(null); loadToday(); loadMatrix(); }} />}
    </div>
  );
}

function SelfieThumb({ rec, label }) {
  const [zoom, setZoom] = useState(false);
  if (!rec) {
    return (
      <div className="w-12 h-12 border-2 border-dashed border-zinc-300 bg-zinc-50 flex items-center justify-center font-mono-ds text-[8px] text-zinc-400" title={`No ${label} yet`}>{label}</div>
    );
  }
  if (!rec.selfie_url) {
    return (
      <div className="w-12 h-12 border-2 border-black bg-zinc-100 flex items-center justify-center font-mono-ds text-[8px] text-zinc-500" title={`${label} · no selfie`}>{label}</div>
    );
  }
  const src = fileUrl(rec.selfie_url);
  const mismatch = rec.face_verified === false;
  return (
    <>
      <button onClick={() => setZoom(true)} type="button" className="block relative" title={mismatch ? `${label} ${rec.time} — FACE MISMATCH (${rec.face_reason || ""})` : `${label} ${rec.time}`}>
        <img src={src} alt={label} className={`w-12 h-12 object-cover border-2 ${mismatch ? "border-[#FF3333]" : "border-black"} hover:shadow-[3px_3px_0_0_#0a0a0a]`} />
        <span className={`absolute -top-1 -right-1 ${mismatch ? "bg-[#FF3333]" : "bg-black"} text-white font-mono-ds text-[7px] px-1 border border-black`}>{mismatch ? "!" : label}</span>
      </button>
      {zoom && (
        <div onClick={() => setZoom(false)} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out" data-testid="selfie-zoom">
          <div className="brut-card bg-white p-2 max-w-md">
            <img src={src} alt={label} className="w-full h-auto border-2 border-black" />
            <div className="p-2 font-mono-ds text-xs">
              <div className="flex justify-between">
                <span>{label} · {rec.time} IST</span>
                <span className="text-zinc-500">Click to close</span>
              </div>
              {rec.face_verified === true && <div className="text-[#22A06B] mt-1">✓ Face verified (similarity {rec.face_similarity})</div>}
              {mismatch && <div className="text-[#FF3333] mt-1">🚨 Face mismatch — {rec.face_reason}</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function SelfieCam({ type, onClose, onDone }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
        setStream(s);
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
      } catch (e) { setErr(e.message || "Camera unavailable — grant permission and try again"); }
    })();
    return () => { if (stream) stream.getTracks().forEach((t) => t.stop()); }; // eslint-disable-line
  }, []); // eslint-disable-line

  function capture() {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    c.width = v.videoWidth || 640; c.height = v.videoHeight || 480;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => setSnap(blob), "image/jpeg", 0.85);
  }
  function retake() { setSnap(null); }

  async function submit() {
    if (!snap) { toast.error("Take a selfie first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", snap, `punch-${Date.now()}.jpg`);
      const up = await api.post("/attendance/upload-selfie", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/attendance/punch", { type, selfie_url: up.data.path, notes });
      toast.success(`${type} logged at ${res.data.time} IST${res.data.is_late ? " (LATE)" : res.data.is_early_leave ? " (early leave)" : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="selfie-modal">
      <div className="brut-card bg-white w-full max-w-lg">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <div className="font-display font-black text-xl inline-flex items-center gap-2">
            <Camera size={18} /> Punch {type} · Selfie
          </div>
          <button onClick={onClose} className="p-1.5 border-2 border-black hover:bg-zinc-100" data-testid="close-selfie"><X size={14} /></button>
        </div>
        <div className="p-4">
          {err && <div className="brut-card-flat bg-[#FFF0F0] p-3 mb-3 text-sm text-[#B10000] flex items-center gap-2"><AlertTriangle size={14} /> {err}</div>}
          {!snap ? (
            <video ref={videoRef} playsInline muted className="w-full aspect-[4/3] object-cover border-2 border-black bg-black" data-testid="selfie-video" />
          ) : (
            <img alt="preview" src={URL.createObjectURL(snap)} className="w-full aspect-[4/3] object-cover border-2 border-black" data-testid="selfie-preview" />
          )}
          <canvas ref={canvasRef} style={{ display: "none" }} />
          <textarea rows={2} className="brut-input mt-3" placeholder="Optional note (e.g. WFH, out for delivery)…" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="selfie-notes" />
        </div>
        <div className="p-4 border-t-2 border-black flex justify-end gap-3">
          <button onClick={onClose} className="brut-btn-outline px-4 py-2" data-testid="cancel-selfie">Cancel</button>
          {!snap ? (
            <button onClick={capture} disabled={!!err} className="brut-btn px-5 py-2 inline-flex items-center gap-2" data-testid="capture-selfie"><Camera size={14} /> Capture</button>
          ) : (
            <>
              <button onClick={retake} className="brut-btn-outline px-4 py-2" data-testid="retake-selfie">Retake</button>
              <button onClick={submit} disabled={busy} className="brut-btn px-5 py-2" data-testid="submit-selfie">{busy ? "Submitting…" : `Confirm ${type}`}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
